import { Link } from 'react-router-dom';
import physiotherapistsData from '@/data/physiotherapists.json';
import type { Physiotherapist } from '@/data/types';
import { WHATSAPP_NUMBER } from '@/data/constants';
import { WhatsAppIcon } from '@/components/icons';

const physiotherapists = physiotherapistsData as Physiotherapist[];

const SERVICES = [
  {
    icon:  'ri-stethoscope-line',
    title: 'Injury Assessment',
    desc:  'Comprehensive evaluation and personalised treatment planning.',
  },
  {
    icon:  'ri-hand-heart-line',
    title: 'Manual Therapy',
    desc:  'Hands-on treatment for pain relief and restored mobility.',
  },
  {
    icon:  'ri-run-line',
    title: 'Performance Recovery',
    desc:  'Optimise recovery between sessions to stay at peak output.',
  },
];

const BOOK_MESSAGE = encodeURIComponent(
  `Hi HUB30! I'd like to book a physiotherapy session.`
);

export default function Physiotherapy() {
  return (
    <section id="physiotherapy" className="relative bg-black text-white overflow-hidden">
      <div className="grid lg:grid-cols-2">

        {/* Left Panel */}
        <div className="py-16 lg:py-24 px-6 lg:px-12 flex flex-col justify-center">
          <div className="max-w-xl mx-auto lg:mx-0">
            <span data-animate className="inline-flex items-center gap-2 text-[#14B8A6] text-xs tracking-widest font-medium mb-4">
              <span className="w-4 h-px bg-[#14B8A6]" />
              RECOVERY
            </span>
            <h2 data-animate data-delay="1" className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-8 leading-tight">
              Physiotherapy<br />&amp; Recovery
            </h2>

            <div className="space-y-6 mb-10">
              {SERVICES.map(({ icon, title, desc }, i) => (
                <div
                  key={title}
                  data-animate
                  data-delay={(i + 2) as 2 | 3 | 4}
                  className="flex items-start gap-4 group"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#14B8A6]/10 border border-[#14B8A6]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#14B8A6]/20 transition-colors">
                    <i className={`${icon} text-[#14B8A6] text-lg`} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold mb-1">{title}</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Book CTA */}
            <a
              data-animate
              data-delay="5"
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${BOOK_MESSAGE}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-7 py-4 rounded-full transition-all duration-300 hover:shadow-[0_0_24px_rgba(37,211,102,0.35)] text-sm"
            >
              <WhatsAppIcon className="text-lg" />
              Book a Session
            </a>
          </div>
        </div>

        {/* Right Panel — Specialists */}
        <div className="py-16 lg:py-24 px-6 lg:px-12 bg-zinc-950 border-l border-white/[0.04]">
          <div className="max-w-lg mx-auto">
            <h3 data-animate className="text-xl lg:text-2xl font-bold mb-8 text-center lg:text-left">
              Our Specialists
            </h3>

            <div className="space-y-4">
              {physiotherapists.map((physio, index) => (
                <Link
                  key={physio.id}
                  to={`/physio/${physio.slug}`}
                  data-animate
                  data-delay={(index + 1) as 1 | 2 | 3}
                  className="bg-[#111] rounded-2xl p-5 border border-white/[0.07] hover:border-[#14B8A6]/30 transition-all duration-300 group block"
                >
                  <div className="flex items-start gap-4">
                    <img
                      src={physio.image}
                      alt={physio.name}
                      className="w-16 h-16 rounded-full object-cover object-top border-2 border-[#14B8A6]/50 group-hover:border-[#14B8A6] transition-colors flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-1.5">
                        <h4 className="text-base font-bold leading-tight">{physio.name}</h4>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#14B8A6]/10 text-[#14B8A6] border border-[#14B8A6]/20 whitespace-nowrap">
                            {physio.specialization.split(',')[0].trim()}
                          </span>
                          <span className="text-[#14B8A6] text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                            View →
                          </span>
                        </div>
                      </div>
                      <p className="text-[#14B8A6] text-xs font-medium mb-2">{physio.specialization}</p>
                      <p className="text-xs text-gray-400 leading-relaxed">{physio.description}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
